import { useState } from "react";
import { Modal } from "./Modal";
import { PastExercise, chartSeries, describePastSet, fellShort } from "../lib/exerciseHistory";

function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/**
 * What the athlete did on this exercise last time, on the working screen.
 *
 * The one number an athlete wants before lifting is what they managed last time. Without it, the reps
 * box is a guess and progress is invisible from inside the workout. A set that came in under what
 * that session asked for gets a dashed outline: shown, not judged.
 *
 * Deliberately no personal bests, badges or "beat last time" nudges (decided 2026-10-04). This is a
 * record of what happened, not a score.
 *
 * Renders nothing the first time an exercise comes up, since there's nothing to show.
 */
export function LastTime({ exerciseName, history }: { exerciseName: string; history: PastExercise[] }) {
  const [open, setOpen] = useState(false);
  const last = history[0];
  if (!last) return null;

  return (
    <section className="last-time" aria-label="Last time">
      <div className="last-time-head">
        <p className="eyebrow">Last time · {shortDate(last.completedAt)}</p>
        <button type="button" className="link-button" onClick={() => setOpen(true)}>
          See history
        </button>
      </div>
      <ul className="set-chips">
        {last.sets.map((set) => (
          <li key={`${set.element_index}-${set.set_index}`} className={fellShort(set, last) ? "set-chip short" : "set-chip"}>
            {describePastSet(set)}
          </li>
        ))}
      </ul>
      {last.note && <p className="last-time-note">{last.note}</p>}

      {open && (
        <Modal title={`${exerciseName} history`} onClose={() => setOpen(false)}>
          <HistoryChart history={history} />
          <ul className="history-list">
            {history.map((past) => (
              <li key={past.sessionId}>
                <span className="history-date">
                  {shortDate(past.completedAt)}
                  {past.note && <span className="history-note">{past.note}</span>}
                </span>
                <span className="history-sets">{past.sets.map(describePastSet).join(" · ")}</span>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setOpen(false)}>
            Done
          </button>
        </Modal>
      )}
    </section>
  );
}

/**
 * A bar per session, oldest on the left, for the last eight. Bars only. A line would suggest values
 * between sessions that never happened.
 */
function HistoryChart({ history }: { history: PastExercise[] }) {
  const series = chartSeries(history.slice(0, 8));
  if (!series || series.points.length < 2) return null;
  const points = [...series.points].reverse();
  const max = Math.max(...points.map((point) => point.value), 1);

  return (
    <figure className="history-chart">
      <figcaption className="eyebrow">{series.label}, last {points.length} sessions</figcaption>
      <ol className="history-bars">
        {points.map((point) => (
          <li key={point.sessionId} aria-label={`${shortDate(point.completedAt)}: ${point.value}`}>
            <span className="history-bar" style={{ height: `${Math.max(6, Math.round((point.value / max) * 100))}%` }} />
            <span className="history-bar-label">{point.value}</span>
          </li>
        ))}
      </ol>
    </figure>
  );
}
